import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentLoader,
  IconCheckmark,
  IconDisabled,
  List,
  StatusCard,
} from 'shared/components';
import { displayValue, formatDate, getLicenseStatus } from 'shared/helpers';
import { fetchLicensesByLicenseUser } from '../../licenses/actions';

const LicenseUserLicenses = ({ licenseUserId }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const handleRowClick = (row) => {
    const licenseId = get(row, 'id');
    if (licenseId) {
      history.push(`/licenses/${licenseId}`);
    }
  };

  useEffect(() => {
    if (!licenseUserId) return;
    setLoading(true);
    setError(false);
    fetchLicensesByLicenseUser(licenseUserId)
      .then((res) => {
        const payload = get(res, 'data');
        const results = Array.isArray(payload)
          ? payload
          : get(payload, 'results', []);
        setLicenses(results);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLicenses([]);
        setLoading(false);
      });
  }, [licenseUserId, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);

  const columns = [
    {
      Header: t('Created on'),
      accessor: 'created_at',
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 140,
    },
    {
      Header: t('License key'),
      id: 'license_key',
      accessor: (row) =>
        get(row, 'license_key') ?? get(row, 'licenseKey') ?? null,
      Cell: (cellData) => displayValue(cellData.value),
      width: 220,
    },
    {
      Header: t('Product'),
      accessor: 'product.product_name',
      Cell: (cellData) => displayValue(cellData.value),
    },
    {
      Header: t('License Type'),
      accessor: 'license_type',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(t(rowData.value));
      },
      width: 120,
    },
    {
      Header: t('Expiration Date'),
      id: 'expiration_date',
      accessor: (row) =>
        get(row, 'expiration_date') ??
        get(row, 'expires_at') ??
        get(row, 'valid_until') ??
        get(row, 'maintenance_period') ??
        null,
      Cell: (cellInfo) => formatDate(cellInfo.value),
      width: 160,
    },
    {
      Header: t('Times activated'),
      accessor: 'times_activated',
      className: 'text-center',
      headerClassName: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        return displayValue(rowData.value, 0);
      },
      width: 140,
    },
    {
      Header: t('Is trial'),
      accessor: 'is_trial',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (cellData) =>
        cellData.value ? (
          <IconCheckmark color="#10ac84" height="14px" />
        ) : (
          <IconDisabled color="#aaa" height="14px" />
        ),
      width: 90,
    },
    {
      Header: t('Status'),
      id: 'status',
      headerClassName: 'text-center',
      className: 'text-center',
      Cell: (rowData) => {
        const isBundle = get(rowData, 'original.is_bundle');
        if (isBundle) return '-';
        const { status, message } = getLicenseStatus(rowData, t);
        return <StatusCard status={status} text={message} />;
      },
      width: 150,
    },
  ];

  if (loading) {
    return <ContentLoader text={t('Loading licenses')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserDetails-error">
        <p>{t('Unable to load licenses for this user')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  if (!licenses.length) {
    return (
      <div className="LicenseUserDetails-empty">
        {t('No licenses for this user')}
      </div>
    );
  }

  return (
    <div className="LicenseUserDetails-list">
      <List
        data={licenses}
        columns={columns}
        minRows={licenses.length || 5}
        showPagination
        loading={loading}
        defaultPageSize={20}
        getTrProps={(state, rowInfo) => ({
          onClick: () => handleRowClick(rowInfo.original),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
};

LicenseUserLicenses.propTypes = {
  licenseUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default LicenseUserLicenses;
